/**
 * SMS credit arithmetic and copy interpolation.
 *
 * The cost of a send is counted here exactly as the backend counts it, so the
 * number the sender reads on the Approvers & Signers step matches the one the
 * send-time check enforces. Getting this wrong is worse than not showing it:
 * a figure that under-counts turns into a refused send with no warning.
 */

import strings from "global/constants/StringConstants";
import { RequestParticipant } from "models/interfaces";
import { SmsCreditCost, SmsCreditPack } from "types/smsCredit";

/** Moved to `helpers/methods` so every feature shares it; re-exported for existing callers. */
export { fillCopy } from "helpers/methods";

/** Whether the signing link reaches this participant by text. */
const receivesBySms = (participant: RequestParticipant): boolean =>
  (participant.deliveryChannels ?? []).includes(
    strings.deliveryChannels.sms as any,
  );

/** Whether this participant verifies with a one-time code sent by text. */
const verifiesBySms = (participant: RequestParticipant): boolean =>
  participant.requestSecurity?.needPhoneValidation === true;

/**
 * Credits a whole request will consume: one per signer receiving the document
 * by text, plus one per signer verifying with an SMS code. A signer doing both
 * costs two — they are two separate messages.
 *
 * In-person signers are excluded: they are handed the device rather than sent
 * a link, so nothing is texted to them.
 *
 * `total` is the floor, not the final bill. A signer who never receives the
 * code can ask for another, and each resend is a further message the wallet
 * pays for — so `maxTotal` carries the ceiling. Only `total` gates a send:
 * blocking on the ceiling would refuse sends the server accepts.
 */
export const getSmsCreditCost = (
  participants?: RequestParticipant[],
): SmsCreditCost => {
  const eligible = (participants ?? []).filter(
    (participant: RequestParticipant) =>
      participant.deliveryMode !== strings.deliveryModes.inPerson,
  );

  const deliveryCredits = eligible.filter(receivesBySms).length;
  const verificationCredits = eligible.filter(verifiesBySms).length;
  const total = deliveryCredits + verificationCredits;
  const resendHeadroom = verificationCredits * strings.SMS_OTP_MAX_RESENDS;

  return {
    deliveryCredits,
    verificationCredits,
    total,
    resendHeadroom,
    maxTotal: total + resendHeadroom,
  };
};

/**
 * Whether the balance should be called out on the send flow. Three reasons,
 * narrowest first: the send doesn't fit at all; it fits but resends could
 * empty the wallet mid-signing; or the wallet is simply low enough that a
 * later level of a sequential request could find it empty.
 *
 * `maxCost` defaults to `cost` so a caller that has no ceiling to pass keeps
 * the old two-reason behaviour.
 */
export const isSmsCreditBalanceLow = (
  balance: number,
  cost: number,
  maxCost: number = cost,
): boolean =>
  cost > 0 &&
  (balance < maxCost ||
    balance <= strings.SMS_CREDIT_LOW_BALANCE_THRESHOLD);

/** Plural-safe "1 credit" / "5 credits". */
export const creditsLabel = (count: number): string =>
  `${count} ${count === 1 ? "credit" : "credits"}`;

/* ── Pack pricing ───────────────────────────────────────────────────────── */

/**
 * A shelf label as text. `POPULAR` / `BEST_VALUE` are the known values, but
 * more may appear, so an unrecognised one is de-underscored and shown rather
 * than dropped — losing the card because its badge is new would be worse than
 * showing a label this build hasn't seen.
 */
export const formatPackBadge = (badge: string): string =>
  badge.replace(/_/g, " ");

/**
 * The tier a quantity reaches: the most expensive pack whose `credits` it
 * meets or exceeds, since the backend prices a typed amount at the rate of
 * the largest pack it reaches. Below the smallest pack it takes that pack's
 * rate, which is the only one on offer at that volume.
 */
const tierFor = (
  quantity: number,
  packs: SmsCreditPack[],
): SmsCreditPack | undefined => {
  if (!packs.length) return undefined;
  const reached = packs.filter((pack) => quantity >= pack.credits);
  if (reached.length) {
    return reached.reduce((best, pack) =>
      pack.credits > best.credits ? pack : best,
    );
  }
  return packs.reduce((cheapest, pack) =>
    pack.credits < cheapest.credits ? pack : cheapest,
  );
};

/**
 * Local mirror of the backend's tier pricing, for a live figure while the
 * quote is in flight. **Never authoritative** — the quote is, and its total is
 * what gets sent as `expectedTotal`.
 *
 * An exact pack quantity is charged that pack's own `price` rather than
 * `credits × unitPrice`, so the two cannot disagree over a rounding rule.
 */
export const localTierTotal = (
  quantity: number,
  packs: SmsCreditPack[],
): number => {
  const exact = packs.find((pack) => pack.credits === quantity);
  if (exact) return exact.price;
  const tier = tierFor(quantity, packs);
  return tier ? quantity * tier.unitPrice : 0;
};

/**
 * A pack that would give **more credits for less money** than the quantity
 * typed — the trap volume tiers create: 999 at $0.050 is $49.95, where the
 * 1,000 pack is $40.00.
 *
 * Returns the largest such pack, or undefined when the typed amount is already
 * the better deal. A pack quantity never triggers this: it is by definition
 * the best price for its own size.
 */
export const betterValuePack = (
  quantity: number,
  total: number,
  packs: SmsCreditPack[],
): SmsCreditPack | undefined => {
  const better = packs.filter(
    (pack) => pack.credits > quantity && pack.price < total,
  );
  if (!better.length) return undefined;
  return better.reduce((best, pack) =>
    pack.credits > best.credits ? pack : best,
  );
};
